// src/components/admin/Storefront.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaUpload, FaEye } from 'react-icons/fa';
import './Storefront.css';

const LINK_OPTIONS = [
  { value: '/shop', label: 'Shop Page' },
  { value: '/shop?sort=new-arrivals', label: 'New Arrivals' },
  { value: '/shop?sort=sale', label: 'Sale' },
];

const Storefront = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null); // 'header-1' | 'header-2' | 'category-1'..'4' | 'bottom'
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [savingCategories, setSavingCategories] = useState(false);
  const [categoriesDraft, setCategoriesDraft] = useState([]); // [{ key, label }]

  const loadCategoryOptions = async () => {
    try {
      const response = await fetch('https://demo-site.storii.com.pk/demo-api/public/get-settings.php');
      const data = await response.json();
      if (data.success && data.settings) {
        let categoriesArray = [];
        let labelsObj = {};
        if (data.settings.categories) {
          try { categoriesArray = JSON.parse(data.settings.categories); } catch (e) { console.error(e); }
        }
        if (data.settings.category_labels) {
          try { labelsObj = JSON.parse(data.settings.category_labels); } catch (e) { console.error(e); }
        }
        setCategoryOptions(categoriesArray);
        setCategoryLabels(labelsObj);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  useEffect(() => {
    loadSettings();
    loadCategoryOptions();
  }, []);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`https://demo-site.storii.com.pk/demo-api/public/get-settings.php?t=${Date.now()}`, { cache: 'no-store' });
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings || {});

        // Parse `categories` — it can be either an ARRAY (old format)
        // or an OBJECT {key:label} (new format from Storefront saves).
        let rawCategories = null;
        try { rawCategories = JSON.parse(data.settings.categories || 'null'); } catch (e) { }

        let keys = [];
        if (Array.isArray(rawCategories)) {
          keys = rawCategories;
        } else if (rawCategories && typeof rawCategories === 'object') {
          keys = Object.keys(rawCategories);
        }

        // Parse labels (object map)
        let labels = {};
        try {
          const parsed = JSON.parse(data.settings.category_labels || '{}');
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            labels = parsed;
          }
        } catch (e) { }

        // If labels object itself has more info than keys, trust labels as source of truth
        if (Object.keys(labels).length > keys.length) {
          keys = Object.keys(labels);
        }

        // HARD CAP at 4 categories
        keys = keys.slice(0, 4);

        // Ensure every key has a friendly label
        const finalizedLabels = { ...labels };
        keys.forEach(k => {
          if (!finalizedLabels[k]) {
            finalizedLabels[k] = k
              .split(/[-_]/)
              .map(w => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ');
          }
        });

        // Only keep labels for our 4 keys
        const trimmedLabels = {};
        keys.forEach(k => { trimmedLabels[k] = finalizedLabels[k]; });

        console.log('Loaded categories (capped at 4):', keys);
        console.log('Loaded category_labels:', trimmedLabels);

        setCategoryOptions(keys);
        setCategoryLabels(trimmedLabels);
        setCategoriesDraft(keys.map(k => ({ key: k, label: trimmedLabels[k] })));
      }
    } catch (e) {
      console.error('loadSettings error:', e);
    } finally {
      setLoading(false);
    }
  };

  const s = (key, fallback = '') => settings[key] ?? fallback;
  const isOn = (key) => settings[key] === '1';

  const openHeaderEdit = (slot) => {
    setEditingBanner(`header-${slot}`);
    setDraft({
      title: s(`slider_title_${slot}`),
      subtitle: s(`slider_subtitle_${slot}`),
      button_text: s(`slider_button_text_${slot}`),
      button_link: s(`slider_button_link_${slot}`, '/shop'),
      image: s(`slider_image_${slot}`),
    });
  };

  const openCategoryEdit = (slot) => {
    setEditingBanner(`category-${slot}`);
    setDraft({
      category: s(`category_banner_${slot}_category`, ''),
      title: s(`category_banner_${slot}_title`),
      description: s(`category_banner_${slot}_description`),
      button_text: s(`category_banner_${slot}_button_text`),
      image: s(`category_banner_${slot}_image`),
    });
  };

  const openBottomEdit = () => {
    setEditingBanner('bottom');
    setDraft({
      eyebrow: s('wardrobe_eyebrow'),
      title: s('wardrobe_title'),
      description: s('wardrobe_description'),
      button_text: s('wardrobe_button_text'),
      image: s('wardrobe_image'),
    });
  };

  const closeEdit = () => { setEditingBanner(null); setDraft({}); };

  const handleBannerImageUpload = async (e, endpoint, extraField) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    if (extraField) formData.append(extraField.name, extraField.value);
    setUploading(true);
    try {
      const res = await fetch(`https://demo-site.storii.com.pk/demo-api/admin/${endpoint}`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setDraft(prev => ({ ...prev, image: data.image_url }));
        toast.success('Image uploaded');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveHeaderBanner = async (slot) => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-header-banner.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, ...draft })
      });
      const data = await res.json();
      if (data.success) { toast.success('Header banner saved'); closeEdit(); loadSettings(); }
      else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const saveCategoryBanner = async (slot) => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-category-banner.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot, ...draft })
      });
      const data = await res.json();
      if (data.success) { toast.success('Category banner saved'); closeEdit(); loadSettings(); }
      else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const saveBottomBanner = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-bottom-banner.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft)
      });
      const data = await res.json();
      if (data.success) { toast.success('Bottom banner saved'); closeEdit(); loadSettings(); }
      else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const saveAnnouncement = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/save-announcement-strip.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          announcement_enabled: isOn('announcement_enabled') ? '1' : '0',
          announcement_text: s('announcement_text')
        })
      });
      const data = await res.json();
      if (data.success) toast.success('Announcement strip saved'); else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const toggleAnnouncement = () => setSettings(prev => ({ ...prev, announcement_enabled: prev.announcement_enabled === '1' ? '0' : '1' }));

  const toggleFeature = (key) => setSettings(prev => ({ ...prev, [key]: prev[key] === '1' ? '0' : '1' }));

  const saveFeatures = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feature_cod_title: s('feature_cod_title', 'Cash on Delivery'),
          feature_cod_description: s('feature_cod_description', 'Pay when your order arrives'),
          feature_delivery_title: s('feature_delivery_title', 'Nationwide Delivery'),
          feature_delivery_description: s('feature_delivery_description', 'Shipping across Pakistan'),
          feature_payment_title: s('feature_payment_title', 'Secure Payments'),
          feature_payment_description: s('feature_payment_description', 'Online payment supported'),
          feature_whatsapp_title: s('feature_whatsapp_title', 'WhatsApp Support'),
          feature_whatsapp_description: s('feature_whatsapp_description', 'Need help? Chat with us'),
        })
      });
      const data = await res.json();
      if (data.success) toast.success('Features section saved'); else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const resolveLinkLabel = (link) => {
    const staticMatch = LINK_OPTIONS.find(o => o.value === link);
    if (staticMatch) return staticMatch.label;
    if (link && link.startsWith('/shop?category=')) {
      const cat = link.replace('/shop?category=', '');
      return categoryLabels[cat] || cat;
    }
    return link || 'Shop Page';
  };

  const openPreview = () => window.open('https://demo-site.storii.com.pk/', '_blank');

  const saveSliderSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          homepage_slider_title: s('homepage_slider_title', 'New Arrivals'),
          homepage_slider_subtitle: s('homepage_slider_subtitle', 'Fresh styles just added to the collection.'),
        })
      });
      const data = await res.json();
      if (data.success) toast.success('Slider settings saved'); else toast.error('Save failed');
    } catch (e) { toast.error('Connection error'); } finally { setSaving(false); }
  };

  // ==================== MANAGE CATEGORIES ====================

  // Convert a label to a URL-safe key (e.g. "Eastern Wear" → "eastern_wear")
  /*
  const slugifyCategoryKey = (label) => {
    return label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };
  */

  const handleCategoryLabelChange = (index, newLabel) => {
    setCategoriesDraft(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], label: newLabel };
      return copy;
    });
  };

  const handleCategoryKeyChange = (index, newKey) => {
    setCategoriesDraft(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], key: newKey };
      return copy;
    });
  };

  const saveCategories = async () => {
    if (categoriesDraft.length === 0) {
      toast.error('No categories to save');
      return;
    }
    if (categoriesDraft.length > 4) {
      toast.error('Maximum 4 categories allowed. Please remove extras.');
      return;
    }
    for (const cat of categoriesDraft) {
      if (!cat.label.trim()) {
        toast.error('Every category needs a name');
        return;
      }
      if (!cat.key.trim()) {
        toast.error('Every category needs a key');
        return;
      }
    }

    const finalKeys = categoriesDraft.map(c => c.key.trim());
    const finalLabels = {};
    categoriesDraft.forEach(c => {
      finalLabels[c.key.trim()] = c.label.trim();
    });

    if (new Set(finalKeys).size !== finalKeys.length) {
      toast.error('Two categories have the same key. Keys must be unique.');
      return;
    }

    // --- Cascade: keep category banners in sync ---
    // Two jobs per banner slot:
    //   1) If the banner's category key was removed/renamed, remap it to the
    //      matching slot position (e.g. slot 3 → 3rd category in the list).
    //   2) Sync the banner's `_title` with the current label for its category,
    //      so renaming in "Manage Categories" propagates to the public homepage.
    const bannerUpdates = {};

    for (let i = 0; i < 4; i++) {
      const bannerKey = `category_banner_${i + 1}_category`;
      const bannerTitleKey = `category_banner_${i + 1}_title`;

      let currentBannerCat = settings[bannerKey] || '';

      // Job 1: remap if the key no longer exists in the new list
      if (!finalKeys.includes(currentBannerCat)) {
        if (finalKeys[i]) {
          currentBannerCat = finalKeys[i];
          bannerUpdates[bannerKey] = currentBannerCat;
        } else {
          // No matching category for this slot — leave as-is
          continue;
        }
      }

      // Job 2: sync the title with the current label
      const expectedTitle = finalLabels[currentBannerCat] || currentBannerCat;
      const currentTitle = settings[bannerTitleKey] || '';
      if (currentTitle !== expectedTitle) {
        bannerUpdates[bannerTitleKey] = expectedTitle;
      }
    }

    const payload = {
      categories: JSON.stringify(finalKeys),
      category_labels: JSON.stringify(finalLabels),
      ...bannerUpdates,
    };

    console.log('Saving categories with cascaded banner updates:', payload);

    setSavingCategories(true);
    try {
      const res = await fetch('https://demo-site.storii.com.pk/demo-api/admin/update-settings-simple.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Categories saved');
        setCategoryOptions(finalKeys);
        setCategoryLabels(finalLabels);
        setSettings(prev => ({
          ...prev,
          categories: JSON.stringify(finalKeys),
          category_labels: JSON.stringify(finalLabels),
          ...bannerUpdates,
        }));
      } else {
        toast.error(data.message || 'Save failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Connection error');
    } finally {
      setSavingCategories(false);
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="storefront-page">
      <div className="sf-header">
        <div>
          <h1>Storefront</h1>
          <p>Manage the content and visuals shown on your online store homepage.</p>
        </div>
        <button className="sf-preview-btn" onClick={openPreview}><FaEye /> Preview Store</button>
      </div>

      {/* Announcement Strip */}
      <section className="sf-card">
        <h2>Announcement Strip</h2>
        <p className="sf-sub">Controls the announcement strip shown at the top of your homepage.</p>

        <label className="sf-toggle-row">
          <span>Announcement Strip Status</span>
          <label className="sf-switch">
            <input type="checkbox" checked={isOn('announcement_enabled')} onChange={toggleAnnouncement} />
            <span className="sf-slider" />
          </label>
        </label>

        <div className="form-group">
          <label>Announcement Strip Text</label>
          <input type="text" value={s('announcement_text')} onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })} />
          <small>Keep this short and clear — delivery offers, sale alerts, or important store updates.</small>
        </div>

        <button className="sf-save-btn" onClick={saveAnnouncement} disabled={saving}>Save Announcement Strip</button>
      </section>

      {/* Homepage Slider */}
      <section className="sf-card">
        <h2>Homepage Slider</h2>
        <p className="sf-sub">Title and subtitle shown above the homepage product slider. Products appear here when "Show in Homepage Product Slider" is turned on in that product's edit page.</p>

        {/* 
        <div className="form-group">
          <label>Slider Title</label>
          <input
            type="text"
            value={s('homepage_slider_title', 'New Arrivals')}
            onChange={(e) => setSettings({ ...settings, homepage_slider_title: e.target.value })}
          />
        </div>
        */}
        <div className="form-group">
          <label>Slider Subtitle</label>
          <input
            type="text"
            value={s('homepage_slider_subtitle', 'Fresh styles just added to the collection.')}
            onChange={(e) => setSettings({ ...settings, homepage_slider_subtitle: e.target.value })}
          />
        </div>

        <button className="sf-save-btn" onClick={saveSliderSettings} disabled={saving}>
          {saving ? 'Saving...' : 'Save Slider Settings'}
        </button>
      </section>

      {/* Header Banners */}
      <section className="sf-card">
        <h2>Header Banners</h2>
        <p className="sf-sub">Manage the two main header banners shown at the top of your homepage.</p>
        <div className="sf-banner-grid">
          {[1, 2].map(slot => (
            <div key={slot} className="sf-banner-card">
              <div className="sf-banner-card-header">
                <span>Header Banner {slot}</span>
                <span className="sf-badge-active">Active</span>
              </div>

              {s(`slider_image_${slot}`) && <img src={s(`slider_image_${slot}`)} alt="" className="sf-banner-img" />}
              <div className="sf-banner-meta">
                <span>Button</span><span>{s(`slider_button_text_${slot}`) || '—'}</span>
                <span>Links to</span><span>{resolveLinkLabel(s(`slider_button_link_${slot}`, '/shop'))}</span>
              </div>

              <div className="sf-banner-actions">
                <button className="sf-outline-btn" onClick={openPreview}><FaEye /> Preview</button>
                <button className="sf-edit-btn" onClick={() => openHeaderEdit(slot)}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ MANAGE CATEGORIES ============ */}
      <section className="sf-card">
        <h2>Manage Categories</h2>
        <p className="sf-sub">
          These are the categories your store sells. They power the navbar, shop page filters,
          and the "Linked Category" dropdown below. You can rename them anytime — for example, if
          you sell jewelry instead of clothing, rename "Shirts" to "Jewelry".
        </p>

        <div className="sf-cat-list">
          {categoriesDraft.map((cat, index) => (
            <div key={index} className="sf-cat-row">
              <div className="sf-cat-fields">
                <div className="form-group">
                  <label>Category Name (shown to customers)</label>
                  <input
                    type="text"
                    value={cat.label}
                    onChange={(e) => handleCategoryLabelChange(index, e.target.value)}
                    placeholder="e.g. Shirts, Jewelry, Furniture"
                  />
                </div>
                <div className="form-group">
                  <label>Key (internal, URL-safe)</label>
                  <input
                    type="text"
                    value={cat.key}
                    onChange={(e) => handleCategoryKeyChange(index, e.target.value)}
                    placeholder="e.g. shirts"
                  />
                  <small className="sf-dim-hint">
                    Used in links like <code>/shop?category=shirts</code>.
                    Changing this after products are assigned will break their links.
                  </small>
                </div>
              </div>
            </div>
          ))}

          {categoriesDraft.length === 0 && (
            <p className="sf-empty-cats">
              No categories found in the database. Add some using the "Categories" settings page first.
            </p>
          )}
        </div>

        {categoriesDraft.length > 0 && (
          <div className="sf-cat-actions">
            <button
              type="button"
              className="sf-save-btn"
              onClick={saveCategories}
              disabled={savingCategories}
            >
              {savingCategories ? 'Saving...' : 'Save Categories'}
            </button>
          </div>
        )}
      </section>

      {/* Category Banners */}
      <section className="sf-card">
        <h2>Category Banners</h2>
        <p className="sf-sub">Manage the 4 category banners shown on your homepage. New Arrivals and Sale are default system pages and cannot be changed here.</p>
        <div className="sf-banner-grid">
          {[1, 2, 3, 4].map(slot => {
            const cat = s(`category_banner_${slot}_category`, '');
            // Three-level fallback: saved label → titlecased key → generic "Category N"
            const displayName =
              categoryLabels[cat] ||
              (cat ? cat.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '') ||
              `Category ${slot}`;
            return (
              <div key={slot} className="sf-banner-card">
                <div className="sf-banner-card-header">
                  <span>Category Banner {slot}</span>
                </div>
                {s(`category_banner_${slot}_image`) && <img src={s(`category_banner_${slot}_image`)} alt="" className="sf-banner-img" />}
                <h4>{displayName}</h4>
                <div className="sf-banner-meta">
                  <span>Button</span><span>{s(`category_banner_${slot}_button_text`) || '—'}</span>
                  <span>Links to</span><span>{categoryLabels[cat] || cat}</span>
                </div>
                <div className="sf-banner-actions">
                  <button className="sf-outline-btn" onClick={openPreview}><FaEye /> Preview</button>
                  <button className="sf-edit-btn" onClick={() => openCategoryEdit(slot)}>Edit</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="sf-card">
        <h2>Features Section</h2>
        <p className="sf-sub">These four cards appear on your homepage and on every product page. Edit each card's title and description below — both locations update automatically.</p>
        <div className="sf-features-grid">
          {[
            ['feature_cod', 'Cash on Delivery', 'Pay when your order arrives'],
            ['feature_delivery', 'Nationwide Delivery', 'Shipping across Pakistan'],
            ['feature_payment', 'Secure Payments', 'Online payment supported'],
            ['feature_whatsapp', 'WhatsApp Support', 'Need help? Chat with us'],
          ].map(([prefix, defaultTitle, defaultDesc]) => (
            <div key={prefix} className="sf-feature-edit-card">
              <div className="form-group">
                <label>Title</label>
                <input
                  value={s(`${prefix}_title`, defaultTitle)}
                  onChange={(e) => setSettings({ ...settings, [`${prefix}_title`]: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input
                  value={s(`${prefix}_description`, defaultDesc)}
                  onChange={(e) => setSettings({ ...settings, [`${prefix}_description`]: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
        <button className="sf-save-btn" onClick={saveFeatures} disabled={saving}>Save Features Section</button>
      </section>

      {/* Bottom / Shop Banner */}
      <section className="sf-card">
        <h2>Bottom / Shop Banner</h2>
        <p className="sf-sub">Manage the promotional banner shown near the bottom of your homepage.</p>
        <div className="sf-banner-card sf-banner-card-single">
          {s('wardrobe_image') && <img src={s('wardrobe_image')} alt="" className="sf-banner-img" />}
          <h4>{s('wardrobe_title') || '—'}</h4>
          <p className="sf-banner-desc">{s('wardrobe_description')}</p>
          <div className="sf-banner-meta">
            <span>Button</span><span>{s('wardrobe_button_text') || '—'}</span>
            <span>Links to</span><span>Shop Page</span>
          </div>
          <div className="sf-banner-actions">
            <button className="sf-outline-btn" onClick={openPreview}><FaEye /> Preview</button>
            <button className="sf-edit-btn" onClick={openBottomEdit}>Edit</button>
          </div>
        </div>
      </section>

      {/* Edit Drawer — shared by header/category/bottom banners */}
      {editingBanner && (
        <div className="sf-drawer-overlay">
          <div className="sf-drawer-backdrop" onClick={closeEdit}></div>
          <div className="sf-drawer">
            <div className="sf-drawer-header">
              <h3>Edit {editingBanner.startsWith('header') ? 'Header Banner' : editingBanner.startsWith('category') ? 'Category Banner' : 'Bottom Banner'}</h3>
              <button onClick={closeEdit}>×</button>
            </div>
            <div className="sf-drawer-body">
              <div className="form-group">
                <label>Image</label>
                {draft.image && <img src={draft.image} alt="" className="sf-drawer-img-preview" />}
                <label className="sf-upload-btn">
                  <FaUpload /> {uploading ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading}
                    onChange={(e) => {
                      if (editingBanner.startsWith('header')) {
                        const slot = editingBanner.split('-')[1];
                        handleBannerImageUpload(e, 'upload-header-banner-image.php', { name: 'slot', value: slot });
                      } else if (editingBanner.startsWith('category')) {
                        const slot = editingBanner.split('-')[1];
                        handleBannerImageUpload(e, 'upload-category-banner-image.php', { name: 'slot', value: slot });
                      } else {
                        handleBannerImageUpload(e, 'upload-wardrobe-image.php');
                      }
                    }}
                  />
                </label>
                {editingBanner.startsWith('header') && (
                  <small className="sf-dim-hint">
                    Recommended size: approx. 1920×800px.
                  </small>
                )}
                {editingBanner.startsWith('category') && (
                  <small className="sf-dim-hint">
                    Recommended size: approx. 800×1000px (portrait, ~4:5 ratio).
                  </small>
                )}
              </div>

              {editingBanner.startsWith('header') && (
                <>
                  <div className="form-group">
                    <label>Title (shown over the image)</label>
                    <input
                      type="text"
                      value={draft.title || ''}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="e.g. New Fits for Everyday Confidence"
                    />
                    <small className="sf-dim-hint">
                      Leave blank to hide the title on the homepage slider.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Subtitle</label>
                    <textarea
                      rows="2"
                      value={draft.subtitle || ''}
                      onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                      placeholder="e.g. Explore shirts, polos, trousers, and essentials designed for modern men."
                    />
                    <small className="sf-dim-hint">
                      Optional — a short supporting line under the title.
                    </small>
                  </div>
                </>
              )}

              {editingBanner.startsWith('category') && (
                <>
                  <div className="form-group">
                    <label>Linked Category</label>
                    <select
                      value={draft.category || ''}
                      onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    >
                      {/* If the currently-saved category isn't in the options list, add it so it doesn't reset */}
                      {draft.category && !categoryOptions.includes(draft.category) && (
                        <option value={draft.category}>
                          {categoryLabels[draft.category] || draft.category}
                        </option>
                      )}
                      {categoryOptions.map(c => (
                        <option key={c} value={c}>{categoryLabels[c] || c}</option>
                      ))}
                    </select>
                    <small className="sf-dim-hint">Controls which shop page this banner links to when clicked.</small>
                  </div>
                  {/* 
                  <div className="form-group">
                    <label>Display Name</label>
                    <input
                      value={draft.display_name}
                      onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
                      placeholder="e.g. Tees & Shirts"
                    />
                    <small className="sf-dim-hint">The name shown on this banner. Independent of the Linked Category above — you can call it whatever you like.</small>
                  </div>
                  */}
                </>
              )}

              {editingBanner.startsWith('bottom') && (
                <div className="form-group">
                  <label>Eyebrow (Small Text)</label>
                  <input
                    value={draft.eyebrow || ''}
                    onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })}
                    placeholder="e.g. The Edit"
                  />
                </div>
              )}

              {!editingBanner.startsWith('header') && (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      value={draft.title || ''}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      placeholder="e.g. Upgrade Your Everyday Wardrobe"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      rows="2"
                      value={draft.description || ''}
                      onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                      placeholder="e.g. From casual essentials to smart staples..."
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Button Text</label>
                <input value={draft.button_text} onChange={(e) => setDraft({ ...draft, button_text: e.target.value })} />
              </div>

              {editingBanner.startsWith('header') && (
                <div className="form-group">
                  <label>Links To</label>
                  <select value={draft.button_link} onChange={(e) => setDraft({ ...draft, button_link: e.target.value })}>
                    <optgroup label="Pages">
                      {LINK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </optgroup>
                    {categoryOptions.length > 0 && (
                      <optgroup label="Categories">
                        {categoryOptions.map(c => (
                          <option key={c} value={`/shop?category=${c}`}>{categoryLabels[c] || c}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              <div className="sf-drawer-actions">
                <button className="sf-outline-btn" onClick={closeEdit}>Cancel</button>
                <button
                  className="sf-save-btn"
                  disabled={saving}
                  onClick={() => {
                    if (editingBanner.startsWith('header')) saveHeaderBanner(editingBanner.split('-')[1]);
                    else if (editingBanner.startsWith('category')) saveCategoryBanner(editingBanner.split('-')[1]);
                    else saveBottomBanner();
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Storefront;